import { computed, defineComponent, onBeforeMount, onBeforeUnmount, ref, } from 'vue';
import useTransactionsStore from '@/store/modules/transactions';
import { usePaymentStore } from '@/store/modules/payment';
import { Button } from '@yellowcard/components-library-vue3';
import { WidgetApiServiceTypes } from '@/types/widgetApiService';
import TransactHeader from '@/components/transact/TransactHeader.vue';
import BankDeposit from '@/components/buyCrypto/BankDepositComponent.vue';
import EFTDepositComponent from '@/components/buyCrypto/EFTDepositComponent.vue';
import LoaderComponent from '@/components/general/LoaderComponent.vue';
import { storeToRefs } from 'pinia';
import { PaymentTypes } from '@/types/payment';
import { useAuthStore } from '@/store/modules/auth';
import { KycTypes } from '@/types/kyc';
import router from '@/router';
import { getTransactionChannelType } from '@/utils/helpers';
import { MIXPANEL_EVENTS, trackEvent } from '@/utils/mixpanel';
var ChannelTypes = WidgetApiServiceTypes.ChannelTypes;
export default defineComponent({
    name: 'DepositView',
    components: {
        TransactHeader,
        BankDeposit,
        Button,
        LoaderComponent,
        EFTDepositComponent,
    },
    setup() {
        const transactionsStore = useTransactionsStore();
        const { selectedChannel, buyFlow, selectedFiatAmountWithoutFees, pollForBankDetails, loadingMessage, userTransactions, collection, } = storeToRefs(transactionsStore);
        const paymentStore = usePaymentStore();
        const { currentStep } = storeToRefs(paymentStore);
        const authStore = useAuthStore();
        const { user, partnerRedirectEnabled, partnerRedirectLink, partnerId } = storeToRefs(authStore);
        const kycStatus = user.value?.kycStatus;
        const loading = ref(false);
        const transactionInterval = ref(null);
        const error = ref(false);
        const errorMessage = ref('');
        let numberOfTransactionPolls = 1;
        const heading = computed(() => {
            if (buyFlow.value) {
                if (selectedChannel.value === ChannelTypes.MOMO) {
                    return 'Depositing money';
                }
                return 'Make your deposit to our bank account';
            }
            return 'Yellow Card Bank Details';
        });
        const startOrderPolling = () => {
            transactionInterval.value = window.setInterval(async () => {
                if (numberOfTransactionPolls <= 9) {
                    try {
                        await transactionsStore.getUserTransactions();
                    }
                    catch (e) {
                        console.error('failed to get user transactions');
                    }
                    numberOfTransactionPolls += 1;
                    if (userTransactions.value[0].bankInfo &&
                        Object.keys(userTransactions.value[0].bankInfo).length !== 0) {
                        // bankInfo has been populated, can stop polling
                        clearInterval(Number(transactionInterval.value));
                        collection.value = userTransactions.value[0];
                        selectedChannel.value = getTransactionChannelType(userTransactions.value[0]);
                        pollForBankDetails.value = false;
                        loading.value = false;
                    }
                }
                else {
                    // polled for 45 seconds, need to stop
                    clearInterval(Number(transactionInterval.value));
                    pollForBankDetails.value = false;
                    error.value = true;
                    errorMessage.value = 'Something went wrong';
                    loading.value = false;
                }
            }, 5000);
        };
        onBeforeMount(async () => {
            if (pollForBankDetails.value && userTransactions.value.length > 0) {
                if (Object.keys(userTransactions.value[0].bankInfo || {}).length === 0) {
                    loadingMessage.value = 'Retrieving deposit details';
                    loading.value = true;
                    startOrderPolling();
                }
                else {
                    collection.value = userTransactions.value[0];
                    pollForBankDetails.value = false;
                }
            }
        });
        onBeforeUnmount(() => {
            pollForBankDetails.value = false;
            error.value = false;
            loading.value = false;
            clearInterval(Number(transactionInterval.value));
        });
        const sendEndFlowMessage = () => {
            // sample message to send
            const messageData = {
                depositMade: true,
                name: 'YCWidgetEndFlowEvent',
                depositAmount: selectedFiatAmountWithoutFees.value,
                origin: `${window.location.protocol}//${window.location.host}`,
                redirectLink: partnerRedirectLink.value,
                redirectEnabled: partnerRedirectEnabled.value,
            };
            const { opener, top } = window;
            // eslint-disable-next-line no-restricted-globals
            if (opener) {
                // eslint-disable-next-line no-restricted-globals
                opener.postMessage(JSON.stringify(messageData), '*');
            }
            else if (top) {
                top.postMessage(JSON.stringify(messageData), '*');
            }
        };
        const onPaymentComplete = (channelType) => {
            if (partnerRedirectEnabled.value) {
                if (partnerRedirectLink.value) {
                    window.location.href = partnerRedirectLink.value;
                }
            }
            else {
                // eslint-disable-next-line no-restricted-globals
                const { top, opener } = window;
                if (top || opener) {
                    sendEndFlowMessage();
                }
            }
            switch (channelType) {
                case ChannelTypes.EFT:
                    trackEvent(MIXPANEL_EVENTS.BUY.EFT_DEPOSIT, {
                        payment_method: 'EFT',
                        partner: partnerId.value,
                    });
                    break;
                case ChannelTypes.MOMO:
                    trackEvent(MIXPANEL_EVENTS.BUY.MOMO_DEPOSIT, {
                        payment_method: 'Momo',
                        partner: partnerId.value,
                    });
                    break;
                default:
                    trackEvent(MIXPANEL_EVENTS.BUY.BANK_DEPOSIT, {
                        payment_method: 'Bank',
                        partner: partnerId.value,
                    });
            }
            currentStep.value = PaymentTypes.PaymentSteps.PAYMENT_4;
        };
        const onClick = () => {
            onPaymentComplete(selectedChannel.value);
        };
        const goBack = () => {
            if (buyFlow.value) {
                currentStep.value = PaymentTypes.PaymentSteps.PAYMENT_1;
            }
            else {
                router.push('/');
            }
        };
        return {
            buyFlow,
            ChannelTypes,
            kycStatus,
            KycTypes,
            onClick,
            goBack,
            selectedChannel,
            heading,
            loading,
            error,
            errorMessage,
        };
    },
});
